import type { InsidedEmbedConfig } from './embed-widget.types'

export function getEmbedGlobal(): InsidedEmbedConfig {
  if (!window.__insidedEmbed) {
    window.__insidedEmbed = { communityUrl: '' }
  }
  return window.__insidedEmbed
}

function loadScript(script: HTMLScriptElement, target: HTMLElement = document.body): Promise<void> {
  return new Promise((resolve, reject) => {
    const newScript = document.createElement('script')

    for (const attr of script.attributes) {
      newScript.setAttribute(attr.name, attr.value)
    }

    if (script.src) {
      newScript.onload = () => resolve()
      newScript.onerror = () => reject(new Error(`Failed to load script: ${script.src}`))
    } else {
      newScript.textContent = script.textContent
    }

    target.appendChild(newScript)

    if (!script.src) {
      resolve()
    }
  })
}

/** IDs of elements from the resources response that we inject into the host page. */
const ALLOWED_ELEMENT_IDS = new Set(['css-variables'])

/**
 * Attributes we copy from <link rel="stylesheet"> when injecting. Excludes event handlers (on*),
 * custom attributes, and anything not needed for safe stylesheet loading.
 * integrity is preserved when present (backend must send correct hashes for SRI to be meaningful).
 */
const ALLOWED_STYLESHEET_ATTRIBUTES = ['rel', 'href', 'integrity', 'crossorigin', 'media', 'nonce'] as const

function isStylesheetLink(link: HTMLLinkElement): boolean {
  const rel = (link.getAttribute('rel') ?? '').trim().toLowerCase()
  const tokens = rel ? rel.split(/\s+/) : []
  return tokens.includes('stylesheet')
}

/** Resolve href to an absolute URL; returns null if empty or invalid. */
function resolveStylesheetUrl(href: string | null): string | null {
  if (href == null) return null
  const trimmed = href.trim()
  if (!trimmed) return null
  try {
    return new URL(trimmed).href
  } catch {
    console.error(`Invalid stylesheet URL: ${href}`)
    return null
  }
}

/**
 * Clone a <link rel="stylesheet"> with only allowlisted attributes so we never inject
 * event handlers or unsafe attributes. Preserves integrity when present. Uses
 * resolvedHref (absolute URL) for the href so relative URLs resolve correctly.
 */
function cloneStylesheetLink(link: HTMLLinkElement, resolvedHref: string): HTMLLinkElement {
  const clone = document.createElement('link')
  for (const name of ALLOWED_STYLESHEET_ATTRIBUTES) {
    const value = name === 'href' ? resolvedHref : link.getAttribute(name)
    if (value != null) clone.setAttribute(name, value)
  }
  return clone
}

async function loadWidgetResources(communityUrl: string): Promise<void> {
  const params = new URLSearchParams()
  const productType = getEmbedGlobal().productType
  if (productType) params.set('productType', productType)

  const qs = params.toString()
  const url = qs ? `${communityUrl}/widget/resources?${qs}` : `${communityUrl}/widget/resources`

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'text/html' }
  })

  if (!response.ok) {
    throw new Error(`Failed to load widget resources: ${response.status}`)
  }

  const html = await response.text()
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const elements = Array.from(doc.head.children)

  const csrfInput = doc.getElementById('csrftoken') as HTMLInputElement | null
  if (csrfInput?.value) {
    getEmbedGlobal().csrfToken = csrfInput.value
  }

  for (const el of elements) {
    await processHeadElement(el)
  }
}

async function processHeadElement(el: Element): Promise<void> {
  if (el.tagName === 'SCRIPT') {
    await loadScript(el as HTMLScriptElement, document.head)
    return
  }
  if (el.tagName === 'LINK' && el instanceof HTMLLinkElement && isStylesheetLink(el)) {
    const resolvedHref = resolveStylesheetUrl(el.getAttribute('href'))
    if (resolvedHref) document.head.appendChild(cloneStylesheetLink(el, resolvedHref))
    return
  }
  if (el instanceof HTMLElement && ALLOWED_ELEMENT_IDS.has(el.id)) {
    document.head.appendChild(el.cloneNode(true))
  }
}

async function loadWidgetResourcesBody(communityUrl: string): Promise<void> {
  const response = await fetch(`${communityUrl}/widget/resourcesBody`, {
    method: 'GET',
    headers: { Accept: 'text/html' }
  })

  if (!response.ok) {
    throw new Error(`Failed to load widget body resources: ${response.status}`)
  }

  const html = await response.text()
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const scripts = Array.from(doc.querySelectorAll('script'))

  for (const script of scripts) {
    await loadScript(script)
  }
}

export async function ensureResourcesLoaded(communityUrl: string): Promise<void> {
  const global = getEmbedGlobal()

  if (global.loaded) return
  if (global.loading) return global.loading

  global.loading = (async () => {
    await loadWidgetResources(communityUrl)
    await loadWidgetResourcesBody(communityUrl)
    global.loaded = true
  })()

  return global.loading
}

export function isResourcesLoaded(): boolean {
  return getEmbedGlobal().loaded === true
}
